import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { Hand, FileText, UserRoundCheck, Sparkles, UserPen, ContactRound, Files, UserRoundCog, Trash2, ScanFace, ShieldQuestion, ShieldCheck } from "lucide-react";
import "../../styles/sidebar.css";
import { AccountSettingsModal, PlanMock } from "../molecules/settings/AccountSettingsModal";
import NewKanbanModal from "../molecules/kanban/NewKanbanModal";
import DeleteJobModal from "../molecules/kanban/modals/DeleteJobModal";
import api from "../../utils/api";
import { clearAllUserState } from "../../utils/session";
import { API_ENDPOINTS, S3_BASE_URL } from "../../config";
import { useUser } from "../../context/UserContext";
import { UserSubscription } from "../../types/subscription";
import { onSubscriptionRefresh, emitSubscriptionRefresh, onKanbansRefresh, emitKanbansRefresh } from "../../utils/subscriptionEvents";

interface Props {
  route?: string;
  onNavigate?: (route: string) => void;
  /** Extra classes — used by PageLayout to turn the sidebar into a mobile drawer. */
  className?: string;
  /** Rendered at the top of the sidebar on mobile (close button). */
  header?: React.ReactNode;
}

interface Kanban {
  uid: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export const Sidebar: FC<Props> = ({ route, onNavigate, className = "", header }) => {
  const { userProfile, updateProfile } = useUser();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [newBoardModalOpen, setNewBoardModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [kanbanToDelete, setKanbanToDelete] = useState<{ uid: string; name: string } | null>(null);
  const [kanbans, setKanbans] = useState<Kanban[]>([]);
  const [loadingKanbans, setLoadingKanbans] = useState(true);
  const [userSubscription, setUserSubscription] = useState<UserSubscription | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const getPlanData = (): PlanMock => {
    if (!userSubscription || !userSubscription.plan) {
      return {
        name: "Free Plan",
        price: "$0/month",
        description: "Perfect for getting started with your job search",
        usage: {
          resumes: "0 / 1",
          packets: "0 / 1",
          tailors: "0 / 3",
          reviews: "0 / 3",
          generations: "0 / 10",
          jobs: "0 / 40",
          contacts: "0 / Unlimited",
          documents: "0 / Unlimited",
        },
      };
    }

    const planCode = userSubscription.plan?.code || userSubscription.plan_code;
    
    return {
      name: userSubscription.plan.name || userSubscription.plan_name || "Unknown Plan",
      price: planCode === 'FREE_PLAN' ? '$0/month' : planCode === 'TRIAL_PLAN' ? 'Trial' : `$${userSubscription.plan.price}/month`,
      description: planCode === 'FREE_PLAN' 
        ? 'Perfect for getting started with your job search'
        : planCode === 'TRIAL_PLAN'
        ? 'Try OfferFlow Pro for free'
        : 'Full access to all features',
      usage: {
        resumes: "0 / 1",
        packets: "0 / 1",
        tailors: "0 / 3",
        reviews: "0 / 3",
        generations: "0 / 10",
        jobs: "0 / 40",
        contacts: "0 / Unlimited",
        documents: "0 / Unlimited",
      },
    };
  };

  const fetchKanbans = useCallback(async () => {
    try {
      setLoadingKanbans(true);
      const data = await api.get<Kanban[] | { kanbans: Kanban[] }>(API_ENDPOINTS.kanbans.list);
      
      if (Array.isArray(data)) {
        setKanbans(data);
      } else if (data && typeof data === 'object' && 'kanbans' in data) {
        setKanbans(data.kanbans);
      } else {
        setKanbans([]);
      }
    } catch (error) {
      setKanbans([]);
    } finally {
      setLoadingKanbans(false);
    }
  }, []);

  useEffect(() => {
    fetchKanbans();
  }, [fetchKanbans]);

  const fetchSubscription = async () => {
    try {
      setLoadingSubscription(true);
      const data = await api.get<UserSubscription>(API_ENDPOINTS.subscriptions.getUserSubscription);
      setUserSubscription(data);
    } catch (error) {
      setUserSubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  useEffect(() => {
    const unsubscribe = onSubscriptionRefresh(() => {
      fetchSubscription();
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onKanbansRefresh(() => {
      fetchKanbans();
    });
    return unsubscribe;
  }, [fetchKanbans]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const handleUserClick = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const handleAccountSettingsClick = () => {
    setUserMenuOpen(false);
    setSettingsModalOpen(true);
  };

  const handleLogoutClick = () => {
    setUserMenuOpen(false);
    clearAllUserState();

    if (typeof window !== 'undefined') {
      window.location.href = '/sign-in';
    }
  };

  const handleNewBoardClick = () => {
    setNewBoardModalOpen(true);
  };

  const handleCreateBoard = async (name: string) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return;
    }
    
    try {
      const newKanban = await api.post<Kanban>(API_ENDPOINTS.kanbans.create, { name });
      setKanbans([...kanbans, newKanban]);
      
      emitSubscriptionRefresh();
      
      emitKanbansRefresh();
    } catch (error: any) {
    }
  };

  const handleDeleteKanban = (kanban: Kanban, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setKanbanToDelete({ uid: kanban.uid, name: kanban.name });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!kanbanToDelete) return;

    try {
      await api.delete(API_ENDPOINTS.kanbans.delete, { 
        kanban_uid: kanbanToDelete.uid 
      });
      
      setKanbans(kanbans.filter(k => k.uid !== kanbanToDelete.uid));
      
      emitSubscriptionRefresh();
      
      emitKanbansRefresh();
      
      setDeleteModalOpen(false);
      setKanbanToDelete(null);
    } catch (error) {
      setDeleteModalOpen(false);
      setKanbanToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setKanbanToDelete(null);
  };

  return (
    <aside className={`sidebar ${className}`.trim()}>
      {header}
      <div className="sidebar__title">
        <img src="/favicon.svg" alt="OfferFlow logo" className="sidebar__logo" />
        <span>OfferFlow</span>
      </div>

      <nav className="sidebar__section">
        <NavLink
          to="/"
          className={({ isActive }) =>
            `sidebar__link ${isActive ? "sidebar__link--active" : ""}`
          }
        >
          <Hand className="sidebar__link-icon" size={16} />
          Welcome
        </NavLink>
      </nav>

      {userProfile?.is_admin && (
        <>
          <div className="sidebar__divider"></div>
          <nav className="sidebar__section">
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `sidebar__link ${isActive ? "sidebar__link--active" : ""}`
              }
            >
              <ShieldCheck className="sidebar__link-icon" size={16} />
              Admin Panel
            </NavLink>
          </nav>
        </>
      )}

      <div className="sidebar__divider"></div>

      <nav className="sidebar__section">
        <NavLink
          to="/resume-builder"
          className={({ isActive }) =>
            `sidebar__link ${isActive ? "sidebar__link--active" : ""}`
          }
        >
          <FileText className="sidebar__link-icon" size={16} />
          Resume Builder
        </NavLink>
        <div className="sidebar__link sidebar__link--disabled">
          <UserRoundCheck className="sidebar__link-icon" size={16} />
          <span className="sidebar__link-label">Resume Review</span>
          <div className="sidebar__link-badge">
            <ShieldQuestion size={16} />
            <div className="sidebar__link-tooltip">
              <div className="sidebar__link-tooltip-title">Coming soon</div>
              <div className="sidebar__link-tooltip-text">
                Resume Review is currently in development and will be available shortly.
              </div>
            </div>
          </div>
        </div>
        <NavLink
          to="/ai-tools"
          className={({ isActive }) =>
            `sidebar__link ${isActive ? "sidebar__link--active" : ""}`
          }
        >
          <Sparkles className="sidebar__link-icon" size={16} />
          AI Tools
        </NavLink>
      </nav>

      <div className="sidebar__divider"></div>

      <nav className="sidebar__section">
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            `sidebar__link ${isActive ? "sidebar__link--active" : ""}`
          }
        >
          <UserPen className="sidebar__link-icon" size={16} />
          Profile
        </NavLink>
        <NavLink
          to="/contacts"
          className={({ isActive }) =>
            `sidebar__link ${isActive ? "sidebar__link--active" : ""}`
          }
        >
          <ContactRound className="sidebar__link-icon" size={16} />
          Contacts
        </NavLink>
        <NavLink
          to="/documents"
          className={({ isActive }) =>
            `sidebar__link ${isActive ? "sidebar__link--active" : ""}`
          }
        >
          <Files className="sidebar__link-icon" size={16} />
          Documents
        </NavLink>
      </nav>

      <div className="sidebar__divider" />

      <div className="sidebar__trackers">
        <div className="sidebar__trackers-left">
          <NavLink to="/dashboard" className="sidebar__trackers-link">
            My Job Trackers
          </NavLink>
          <span title="Your personal job search" className="sidebar__help">
            ?
          </span>
        </div>
        <button className="sidebar__plus" onClick={handleNewBoardClick}>+</button>
      </div>

      {loadingKanbans ? (
        <div className="sidebar__tracker-item" style={{ opacity: 0.6, cursor: 'default' }}>
          <span>Loading boards...</span>
        </div>
      ) : Array.isArray(kanbans) && kanbans.length > 0 ? (
        kanbans.map((kanban) => (
          <NavLink
            key={kanban.uid}
            to={`/track/board/${kanban.uid}`}
            className="sidebar__tracker-item"
          >
            <span>{kanban.name}</span>
            <button
              type="button"
              className="sidebar__trash"
              onClick={(e) => handleDeleteKanban(kanban, e)}
              title="Delete board"
            >
              <Trash2 size={14} />
            </button>
          </NavLink>
        ))
      ) : (
        <div className="sidebar__tracker-item" style={{ opacity: 0.6, cursor: 'default' }}>
          <span>No boards yet. Create one!</span>
        </div>
      )}

      {(() => {
        const showGetPro = !loadingSubscription && userSubscription && 
          (userSubscription.plan?.code === 'FREE_PLAN' || userSubscription.plan?.code === 'TRIAL_PLAN');
        
        return (
          <>
            {showGetPro && (
              <a href="/settings/plan/purchase" className="sidebar__pro">
                <div className="sidebar__pro-title">OfferFlow Pro</div>
                <div className="sidebar__pro-sub">Level up your job search</div>
                <div className="sidebar__pro-btn">Get Pro</div>
              </a>
            )}

            <div 
              ref={userMenuRef}
              className={`sidebar__user ${!showGetPro ? 'sidebar__user--bottom' : ''}`}
              onClick={handleUserClick}
            >
              {userProfile?.img_url ? (
                <img 
                  src={userProfile.img_url.startsWith('http')
                    ? userProfile.img_url
                    : `${S3_BASE_URL}${userProfile.img_url}`
                  }
                  alt="Avatar" 
                  className="sidebar__user-avatar sidebar__user-avatar--img" 
                />
              ) : (
                <div className="sidebar__user-avatar sidebar__user-avatar--empty">
                  <ScanFace size={16} />
                </div>
              )}
              <span className="sidebar__user-name">
                {userProfile?.first_name || userProfile?.last_name 
                  ? [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ')
                  : 'Hi, there!'}
              </span>
              <UserRoundCog className="sidebar__user-settings" size={16} />
              
              {userMenuOpen && (
                <div className="sidebar__user-popup">
                  <button className="sidebar__user-popup-item" onClick={handleAccountSettingsClick}>
                    Account settings
                  </button>
                  <button className="sidebar__user-popup-item" onClick={handleLogoutClick}>
                    Log Out
                  </button>
                </div>
              )}
            </div>
          </>
        );
      })()}

      <AccountSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        plan={getPlanData()}
        profile={{
          firstName: userProfile?.first_name || "",
          lastName: userProfile?.last_name || "",
          email: userProfile?.email || "",
          isEmailConfirmed: userProfile?.is_email_confirmed ?? false,
          weeklyDigest: userProfile?.weekly_digest ?? false,
          dailyDigest: userProfile?.daily_digest ?? false,
          imgUrl: userProfile?.img_url || "",
        }}
        onProfileUpdate={(firstName, lastName) => {
          updateProfile({ first_name: firstName, last_name: lastName });
        }}
        onPhotoUpdate={(imgUrl) => {
          updateProfile({ img_url: imgUrl });
        }}
        userSubscription={userSubscription}
      />

      <NewKanbanModal
        open={newBoardModalOpen}
        onClose={() => setNewBoardModalOpen(false)}
        onCreate={handleCreateBoard}
      />

      <DeleteJobModal
        open={deleteModalOpen}
        title="Delete Board"
        message={`Are you sure you want to delete "${kanbanToDelete?.name}"? All jobs, notes, and activities associated with this board will be permanently deleted.`}
        confirmText="Delete Board"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </aside>
  );
};
